import React, { ReactNode } from 'react';
import { Card, Row, Col, Avatar, Space, Divider, Button, Popover, message } from 'antd';
import { UserOutlined, CalendarOutlined, RightOutlined, ShareAltOutlined, DeleteOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
    FacebookShareButton,
    WhatsappShareButton,
    TelegramShareButton,
    TwitterShareButton,
    LineShareButton,
    FacebookIcon,
    WhatsappIcon,
    TelegramIcon,
    XIcon,
    LineIcon
} from 'react-share';
import { downloadFileFetch } from '../../../helpers/download';
import { FileProperties } from '../../../types/file.type';

interface PropertyCardProps {
    avatarSrc?: string;
    userName?: string;
    officeName?: string;
    postingType?: {
        label: string;
        bgColor: string;
        textColor: string;
    };
    propertyType?: string;
    createdAt: string | Date;
    title: string | ReactNode;
    propertyInfo?: ReactNode;
    onViewDetail: () => void;
    onContact: () => void;
    isContactDisabled?: boolean;
    isPrimaryProject?: boolean;
    presentationFile?: FileProperties;
    priceListFile?: FileProperties;
    shareUrl: string;
    shareText: string;
    showDelete?: boolean;
    onDelete?: () => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
    avatarSrc,
    userName,
    officeName,
    postingType,
    propertyType,
    createdAt,
    title,
    propertyInfo,
    onViewDetail,
    onContact,
    isContactDisabled = false,
    isPrimaryProject = false,
    presentationFile,
    priceListFile,
    shareUrl,
    shareText,
    showDelete = false,
    onDelete,
}) => {
    return (
        <Card
            hoverable
            onClick={onViewDetail}
            style={{
                borderRadius: '12px',
                border: '1px solid #f0f0f0',
                cursor: 'pointer',
            }}
            bodyStyle={{ padding: 16 }}
        >
            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col flex="48px">
                    <Avatar
                        size={48}
                        src={avatarSrc}
                        icon={<UserOutlined />}
                        style={{ backgroundColor: '#1890ff' }}
                    />
                </Col>
                <Col flex="auto">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '15px', color: '#000', marginBottom: 2 }}>
                                {userName}
                            </div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                                {officeName || "REMAX"}
                            </div>
                        </div>
                        {postingType && (
                            <div
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: '100px',
                                    backgroundColor: postingType.bgColor,
                                    color: postingType.textColor,
                                    fontSize: '12px',
                                    fontWeight: 600
                                }}
                            >
                                {postingType.label}
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            <Row gutter={14} style={{ marginBottom: 12 }}>
                <Col>
                    <Space size={4} style={{ fontSize: '12px', color: '#999' }}>
                        <img src="/images/bookmark.svg" alt="property type" style={{ width: 14, height: 14 }} />
                        <span>{propertyType || "Rumah"}</span>
                    </Space>
                </Col>
                <Col>
                    <Space size={4} style={{ fontSize: '12px', color: '#999' }}>
                        <CalendarOutlined />
                        <span>{moment(createdAt).format('DD MMM YYYY, HH:mm')}</span>
                    </Space>
                </Col>
            </Row>

            <div
                style={{
                    fontSize: '13px',
                    color: '#333',
                    lineHeight: '1.5',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: '60px',
                    marginBottom: '10px'
                }}
            >
                {typeof title === 'string' ? (
                    <div dangerouslySetInnerHTML={{ __html: title.replace(/<p>/g, '<p style="margin:0;padding:0">') }} />
                ) : (
                    title
                )}
            </div>

            {propertyInfo && (
                <>
                    <Divider type="horizontal" style={{ margin: "0" }} />
                    {propertyInfo}
                    <Divider type="horizontal" style={{ margin: "0" }} />
                </>
            )}

            <Row justify="space-between" align="middle" style={{ minHeight: '32px', padding: '10px 0' }}>
                <Button
                    type="link"
                    style={{
                        padding: 0,
                        height: 'auto',
                        fontSize: '12px',
                        color: 'black'
                    }}
                >
                    View Detail <RightOutlined style={{ fontSize: '10px' }} />
                </Button>
                <Space size={8} onClick={(e) => e.stopPropagation()}>
                    {showDelete && onDelete && (
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            style={{
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={onDelete}
                        />
                    )}

                    {isPrimaryProject && (
                        <Popover
                            placement="top"
                            trigger="click"
                            overlayInnerStyle={{
                                border: "2px solid #E3E9F4",
                                borderRadius: "10px",
                                padding: "4px",
                            }}
                            content={
                                <>
                                    {!presentationFile && !priceListFile ? (
                                        <div style={{ padding: "8px 12px", color: "#666" }}>
                                            There are no documents to download.
                                        </div>
                                    ) : (
                                        <Space direction='horizontal' size={10}>
                                            {presentationFile && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (presentationFile.fileUrl) {
                                                            downloadFileFetch(presentationFile.fileUrl, `${presentationFile?.originalName}` || "");
                                                        }
                                                    }}
                                                    type="link"
                                                    style={{ paddingLeft: 0, paddingRight: 0 }}
                                                    icon={<DownloadOutlined />}
                                                    size="small"
                                                >
                                                    PK
                                                </Button>
                                            )}

                                            {priceListFile && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (priceListFile.fileUrl) {
                                                            downloadFileFetch(priceListFile.fileUrl, `${priceListFile?.originalName}` || "");
                                                        }
                                                    }}
                                                    type="link"
                                                    style={{ paddingLeft: 0, paddingRight: 0 }}
                                                    icon={<DownloadOutlined />}
                                                    size="small"
                                                >
                                                    PRICE LIST
                                                </Button>
                                            )}
                                        </Space>
                                    )}
                                </>
                            }
                        >
                            <Button
                                type='primary'
                                icon={
                                    <DownloadOutlined />
                                }
                                size="small"
                                style={{
                                    backgroundColor: 'transparent',
                                    color: 'black',
                                    border: 'none',
                                    boxShadow: 'none',
                                }}
                            />
                        </Popover>
                    )}

                    <Popover
                        placement="top"
                        trigger="click"
                        overlayInnerStyle={{
                            border: "2px solid #E3E9F4",
                            borderRadius: "10px",
                            padding: "4px",
                        }}
                        content={
                            <Space align="start">
                                <FacebookShareButton url={shareUrl} title={shareText}>
                                    <FacebookIcon size={32} round />
                                </FacebookShareButton>
                                <WhatsappShareButton url={shareUrl} title={shareText}>
                                    <WhatsappIcon size={32} round />
                                </WhatsappShareButton>
                                <TelegramShareButton url={shareUrl} title={shareText}>
                                    <TelegramIcon size={32} round />
                                </TelegramShareButton>
                                <TwitterShareButton url={shareUrl} title={shareText}>
                                    <XIcon size={32} round />
                                </TwitterShareButton>
                                <LineShareButton url={shareUrl} title={shareText}>
                                    <LineIcon size={32} round />
                                </LineShareButton>
                                <Button
                                    icon={<LinkOutlined />}
                                    shape="circle"
                                    style={{
                                        width: 32,
                                        height: 32,
                                        minWidth: 32,
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#0088cc',
                                        borderColor: '#0088cc',
                                        color: 'white'
                                    }}
                                    onClick={() => {
                                        navigator.clipboard.writeText(shareUrl);
                                        message.success('Link copied to clipboard!');
                                    }}
                                />
                            </Space>
                        }
                    >
                        <Button
                            icon={<ShareAltOutlined />}
                            size="small"
                            style={{
                                backgroundColor: 'transparent',
                                color: 'black',
                                border: 'none',
                                boxShadow: 'none',
                            }}
                        />
                    </Popover>
                    <Button
                        type="primary"
                        size="small"
                        onClick={onContact}
                        disabled={isContactDisabled}
                        style={{
                            padding: '6px 10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#0f1f3d',
                            borderColor: '#0f1f3d',
                            borderRadius: '8px'
                        }}
                    >
                        Contact
                    </Button>
                </Space>
            </Row>
        </Card >
    );
};

export default PropertyCard;
